import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map } from 'rxjs';
import { ApiService } from '../../core/services/api.service';
import { ApiEnvelope } from '../models/content.models';
import {
  LeadDto,
  LeadStatusOption,
  LeadSubStatusOption,
  LeadTransitionOption,
  CreateLeadPayload,
  LeadExportRow,
  ListLeadsParams,
  PaginatedLeads,
  TransitionLeadPayload,
  UpdateLeadPayload,
  WorkflowDocumentation,
} from './leads.service';

function toQuery(params: ListLeadsParams): string {
  const q = new URLSearchParams();
  if (params.page) q.set('page', String(params.page));
  if (params.pageSize) q.set('pageSize', String(params.pageSize));
  if (params.search?.trim()) q.set('search', params.search.trim());
  if (params.statusId) q.set('statusId', params.statusId);
  if (params.preferredDestination?.trim()) q.set('preferredDestination', params.preferredDestination.trim());
  if (params.stream?.trim()) q.set('stream', params.stream.trim());
  if (params.section?.trim()) q.set('section', params.section.trim());
  if (params.createdFrom) q.set('createdFrom', params.createdFrom);
  if (params.createdTo) q.set('createdTo', params.createdTo);
  if (params.archiveFilter) q.set('archiveFilter', params.archiveFilter);
  if (params.ownerUserId) q.set('ownerUserId', params.ownerUserId);
  if (params.showEnrolled) q.set('showEnrolled', 'true');
  if (params.showDisqualified) q.set('showDisqualified', 'true');
  const s = q.toString();
  return s ? `?${s}` : '';
}

@Injectable({ providedIn: 'root' })
export class TenantLeadsService {
  private readonly http = inject(HttpClient);

  constructor(private readonly api: ApiService) {}

  list(params: ListLeadsParams = {}) {
    return this.api
      .get<ApiEnvelope<PaginatedLeads>>(`tenant/leads${toQuery(params)}`)
      .pipe(map((r) => r.data));
  }

  listStatuses() {
    return this.api
      .get<ApiEnvelope<LeadStatusOption[]>>('tenant/leads/statuses')
      .pipe(map((r) => r.data));
  }

  getById(id: string) {
    return this.api.get<ApiEnvelope<LeadDto>>(`tenant/leads/${id}`).pipe(map((r) => r.data));
  }

  update(id: string, payload: UpdateLeadPayload) {
    return this.api.patch<ApiEnvelope<LeadDto>>(`tenant/leads/${id}`, payload).pipe(map((r) => r.data));
  }

  uploadProfilePhoto(id: string, file: File) {
    const form = new FormData();
    form.append('file', file);
    return this.http
      .post<ApiEnvelope<LeadDto>>(`${this.api.baseUrl}/tenant/leads/${id}/profile-photo`, form, {
        withCredentials: true,
      })
      .pipe(map((r) => r.data));
  }

  archive(id: string) {
    return this.api.post<ApiEnvelope<LeadDto>>(`tenant/leads/${id}/archive`, {}).pipe(map((r) => r.data));
  }

  unarchive(id: string) {
    return this.api.post<ApiEnvelope<LeadDto>>(`tenant/leads/${id}/unarchive`, {}).pipe(map((r) => r.data));
  }

  getTransitions(id: string) {
    return this.api
      .get<ApiEnvelope<LeadTransitionOption[]>>(`tenant/leads/${id}/transitions`)
      .pipe(map((r) => r.data));
  }

  getWorkflowDocumentation() {
    return this.api
      .get<ApiEnvelope<WorkflowDocumentation>>('tenant/leads/workflow')
      .pipe(map((r) => r.data));
  }

  transition(id: string, payload: TransitionLeadPayload) {
    return this.api
      .post<ApiEnvelope<LeadDto>>(`tenant/leads/${id}/transition`, payload)
      .pipe(map((r) => r.data));
  }

  listSubStatuses() {
    return this.api
      .get<ApiEnvelope<LeadSubStatusOption[]>>(`tenant/leads/sub-statuses`)
      .pipe(map((r) => r.data));
  }

  updateSubStatus(id: string, subStatusId: string | null) {
    return this.api
      .patch<ApiEnvelope<LeadDto>>(`tenant/leads/${id}/sub-status`, { subStatusId })
      .pipe(map((r) => r.data));
  }

  create(payload: CreateLeadPayload) {
    return this.api.post<ApiEnvelope<LeadDto>>('tenant/leads', payload).pipe(map((r) => r.data));
  }

  assignOwner(id: string, ownerUserId: string) {
    return this.api
      .post<ApiEnvelope<LeadDto>>(`tenant/leads/${id}/assign`, { ownerUserId })
      .pipe(map((r) => r.data));
  }

  remove(id: string) {
    return this.api.delete<ApiEnvelope<{ deleted: boolean }>>(`tenant/leads/${id}`).pipe(map((r) => r.data));
  }

  exportLeads(params: ListLeadsParams = {}) {
    return this.api
      .get<ApiEnvelope<LeadExportRow[]>>(`tenant/leads/export${toQuery(params)}`)
      .pipe(map((r) => r.data));
  }

  getImportFieldDefinitions() {
    return this.api
      .get<ApiEnvelope<import('../models/lead-import.model').LeadImportFieldDefinition[]>>(
        'tenant/leads/import/field-definitions',
      )
      .pipe(map((r) => r.data));
  }

  previewImport(payload: {
    columnMapping: Record<string, string>;
    rows: { data: Record<string, string> }[];
    streamValueMappings?: Record<string, string>;
  }) {
    return this.api
      .post<ApiEnvelope<import('../models/lead-import.model').LeadImportPreviewResult>>(
        'tenant/leads/import/preview',
        payload,
      )
      .pipe(map((r) => r.data));
  }

  executeImport(payload: {
    columnMapping: Record<string, string>;
    rows: { data: Record<string, string> }[];
    streamValueMappings?: Record<string, string>;
    duplicateAction: import('../models/lead-import.model').LeadImportDuplicateAction;
    includeWarnings: boolean;
  }) {
    return this.api
      .post<ApiEnvelope<import('../models/lead-import.model').LeadImportExecuteResult>>(
        'tenant/leads/import/execute',
        payload,
      )
      .pipe(map((r) => r.data));
  }

  previewDuplicates() {
    return this.api
      .get<ApiEnvelope<import('../models/lead-duplicates.model').LeadDuplicatesPreview>>(
        'tenant/leads/duplicates/preview',
      )
      .pipe(map((r) => r.data));
  }

  removeDuplicates(leadIds: string[]) {
    return this.api
      .post<ApiEnvelope<import('../models/lead-duplicates.model').LeadDuplicatesRemoveResult>>(
        'tenant/leads/duplicates/remove',
        { leadIds },
      )
      .pipe(map((r) => r.data));
  }

  previewMerge(leadIds: string[]) {
    return this.api
      .post<ApiEnvelope<import('../models/lead-merge.model').LeadMergePreview>>(
        'tenant/leads/merge/preview',
        { leadIds },
      )
      .pipe(map((r) => r.data));
  }

  executeMerge(payload: {
    masterLeadId: string;
    sourceLeadIds: string[];
    fieldSelections: Record<string, string>;
    expectedUpdatedAt?: Record<string, string>;
    clientMergeId?: string;
  }) {
    return this.api
      .post<ApiEnvelope<import('../models/lead-merge.model').LeadMergeExecuteResult>>(
        'tenant/leads/merge/execute',
        payload,
      )
      .pipe(map((r) => r.data));
  }
}
